/**
 * The links experts relied on. The backend stores them on the debate and each
 * step lists the ids of its own (see functions/src/citations.ts).
 */
export type SourceCheck = "pending" | "ok" | "unchecked" | "dead";

export interface DebateSource {
  id: string;
  url: string;
  title: string;
  domain: string;
  via: "search" | "text";
  check: SourceCheck;
}

const CHECKS: SourceCheck[] = ["pending", "ok", "unchecked", "dead"];

/** Valid http(s) sources from stored data; anything else is dropped. */
export function parseSources(raw: unknown): DebateSource[] {
  if (!Array.isArray(raw)) return [];
  const sources: DebateSource[] = [];
  for (const entry of raw as any[]) {
    if (typeof entry?.id !== "string" || typeof entry?.url !== "string") continue;
    let host: string;
    try {
      const url = new URL(entry.url);
      if (url.protocol !== "http:" && url.protocol !== "https:") continue;
      host = url.hostname.replace(/^www\./, "");
    } catch {
      continue;
    }
    sources.push({
      id: entry.id,
      url: entry.url,
      title: typeof entry.title === "string" && entry.title ? entry.title : host,
      domain: typeof entry.domain === "string" && entry.domain ? entry.domain : host,
      via: entry.via === "text" ? "text" : "search",
      check: CHECKS.includes(entry.check) ? entry.check : "unchecked",
    });
  }
  return sources;
}

/**
 * A dead link is never shown. A link only the model typed, with no search
 * result behind it, is shown only once it has been confirmed to open.
 */
export function visibleSources(sources: DebateSource[]): DebateSource[] {
  return sources.filter((s) => s.check !== "dead" && (s.via === "search" || s.check === "ok"));
}

/** The visible sources a step listed, in the step's order. */
export function sourcesForStep(ids: unknown, sources: DebateSource[]): DebateSource[] {
  if (!Array.isArray(ids)) return [];
  const visible = new Map(visibleSources(sources).map((s) => [s.id, s]));
  return ids.map((id) => visible.get(id)).filter((s): s is DebateSource => !!s);
}
